import { Link } from '@material-ui/core';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';

import { IResellerConfig } from '~/config/shared/reseller-config';
import { EBool } from '~/src/dash/api/types/bool';
import {
  Col,
  Cols,
  Div,
  Row,
  Rows,
  spacing,
  Text,
  usePanel,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { getLogoURL } from '~/src/dash/resellers/utils/importResellerConfig';
import { RouteMobileHome } from '~/src/dash/router/routes';
import { IS_AUTHORIZED_OR } from '~/src/dash/utils/permission_service';
import { hasRoleSuperUser } from '~/src/dash/utils/roles';
import { isFirstPartyEnabled } from '~/src/dash/utils/ui';
import { useBaseState, useBaseStore } from '~/src/dash/views/base/contexts';
import { INavBarConfig } from '~/src/dash/views/navbar/configs/defaultCfg';
import { NavBarContext } from '~/src/dash/views/navbar/contexts';
import { isDarkTheme, missingPermToText } from '~/src/dash/views/navbar/NavBar/common/consts';
import { ExpandableNavBarRow } from '~/src/dash/views/navbar/NavBar/ExpandableNavBarRow';
import { NavBarRow } from '~/src/dash/views/navbar/NavBar/NavBarRow';
import { SelectPublisherView } from '~/src/dash/views/select-publisher-view';

interface INavBarProps {
  cfg: INavBarConfig;
  handleNavExpand: () => void;
  setIsScrollbarVisible: React.Dispatch<React.SetStateAction<boolean>>;
}

export const NavBar = ({ cfg, handleNavExpand, setIsScrollbarVisible }: INavBarProps) => {
  // context related
  const { session, user, isDemoUser } = useBaseStore();
  const { t } = usePublicaTranslation();
  const isSuperUser = hasRoleSuperUser(user.user);
  const isMeasurementEnabled =
    isFirstPartyEnabled(session.publisher) ||
    session.publisher.EnableIASThirdPartyVASTTracking === EBool.EBool_TRUE;
  const { isNavExpanded } = useBaseState();
  const { theme, height } = usePanel();

  // states and refs
  const [activeSubMenu, setActiveSubMenu] = useState('');
  const [refHeight, setRefHeight] = useState(0);
  const [resizeAction, setResizeAction] = useState<Record<string, string>>({ action: 'none' });
  const ref = useRef(null);

  // eslint-disable-next-line
  const RESELLER_CONFIG = (window as any).CONFIG as IResellerConfig;
  const permissionParams = {
    session,
    user,
    isDemoUser,
    isSuperUser,
    isMeasurementEnabled,
  };

  // the following effects are to track the component heights to determine if the scrollbar is visible
  useLayoutEffect(() => {
    if (ref.current) {
      // @ts-ignore
      setRefHeight(ref.current.clientHeight);
    }
  }, [resizeAction, isNavExpanded, ref.current]);

  const isScrollbarVisible = (height || 0) <= refHeight;

  useEffect(() => {
    setIsScrollbarVisible(isScrollbarVisible);
  }, [isScrollbarVisible]);

  return (
    <NavBarContext.Provider
      value={{
        cfg,
        resellerCfg: RESELLER_CONFIG,
        isDarkTheme: isDarkTheme(RESELLER_CONFIG.resellerID),
        activeSubMenu,
        setActiveSubMenu,
        resizeAction,
        setResizeAction,
      }}
    >
      <Rows
        paddingBottom={spacing.extraLarge}
        style={{
          position: 'absolute',
          backgroundColor: theme.menuBarBackground,
          color: theme.menuBarText,
          width: isNavExpanded ? '220px' : '64px',
          fontFamily: 'Roboto',
        }}
        innerRef={ref}
      >
        <Row height={78} style={{ padding: '32px 16px 16px' }}>
          <Cols style={{ alignItems: 'center' }}>
            {RESELLER_CONFIG.resellerID === -1 ? (
              <>
                <Col>
                  <img
                    src='/assets/publica/navbar-logo/publicaIcon.png'
                    width='auto'
                    height='25px'
                  />
                </Col>
                {isNavExpanded && (
                  <Col flex paddingLeft={spacing.small}>
                    <img
                      // src='/assets/publica/navbar-logo/publicaTextTeal.png'
                      src='/assets/publica/navbar-logo/santa_pub.png' // #HOLIDAYTHEME
                      width='auto'
                      height='25px'
                    />
                  </Col>
                )}
              </>
            ) : (
              <Col
                flex
                style={{
                  height: '40px',
                  textAlign: 'center',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <img
                  src={getLogoURL()}
                  style={{
                    width: isNavExpanded ? '100%' : '30px',
                    height: isNavExpanded ? '100%' : 'auto',
                    maxHeight: '40px',
                    objectFit: 'contain',
                    overflow: 'hidden',
                    margin: 'auto',
                  }}
                  alt=''
                />
              </Col>
            )}
            {isNavExpanded && (
              <Col style={{ textAlign: 'right' }}>
                <img
                  src='/assets/shared/svg/navbar/collapseLeft16.svg'
                  width='auto'
                  height='16px'
                  style={{ cursor: 'pointer' }}
                  onClick={handleNavExpand}
                />
              </Col>
            )}
          </Cols>
        </Row>
        <Row paddingTop={spacing.none} paddingBottom={spacing.large} height={72}>
          {isNavExpanded ? (
            <SelectPublisherView />
          ) : (
            <div
              style={{
                width: '100%',
                paddingTop: spacing.medium,
                textAlign: 'center',
              }}
            >
              <img
                src='/assets/shared/svg/navbar/collapseRight16.svg'
                width='auto'
                height='16px'
                style={{ cursor: 'pointer' }}
                onClick={handleNavExpand}
              />
            </div>
          )}
        </Row>
        {Object.values(cfg.sections).map((section, i) => {
          if (section.label === 'Admin' && !isSuperUser) {
            return;
          }
          return (
            <>
              {i === 1 && (
                <Row horizontalPadding noVerticalPadding>
                  <div
                    style={{
                      minHeight: '16px',
                      borderBottom: `1px solid ${theme.menuBarText}`,
                      opacity: isNavExpanded ? 0.2 : 0,
                    }}
                  ></div>
                </Row>
              )}
              {i !== 0 && (
                <Row
                  displayFlex
                  largeHorizontalPadding
                  paddingBottom='2px'
                  height={32}
                  style={{ alignItems: 'flex-end' }}
                >
                  {isNavExpanded && section.isLabelDisplayed && (
                    <Text mini color={theme.menuBarText} tKey={section.tKey}>
                      {section.label}
                    </Text>
                  )}
                </Row>
              )}
              {section.items.map((item) => {
                let label = item.tKey ? t(item.tKey) : item.label;
                if (
                  item.checkIfAddSuperUserAsteriskToLabel &&
                  item.checkIfAddSuperUserAsteriskToLabel(permissionParams)
                ) {
                  label += ' *';
                }
                // filter here
                // if the check fails, skip rendering the item
                if (item.checkIfHidden && item.checkIfHidden(permissionParams)) {
                  return;
                }

                // TODO: if time, we should refactor these previous functions to be more intuitive
                const isDisabled =
                  item.permChecks && item.permChecks.length > 0
                    ? missingPermToText(IS_AUTHORIZED_OR(item.permChecks, user.user)) !== undefined
                    : false;
                const tooltipText: React.ReactNode =
                  item.permChecks && item.permChecks.length > 0 ? (
                    missingPermToText(IS_AUTHORIZED_OR(item.permChecks, user.user))
                  ) : (
                    <div>{label}</div>
                  );
                // this chain displays the correct type by checking the available props
                if (item.isSubMenu && item.subViews && item.subItems) {
                  return (
                    <Row noVerticalPadding marginTop='1px'>
                      <ExpandableNavBarRow
                        label={label}
                        iconPath={item.iconPath}
                        subViews={item.subViews}
                        subItems={item.subItems}
                        tooltipText={tooltipText}
                        isDisabled={isDisabled}
                        iconPathDark={item.iconPathDark}
                        handleNavExpand={handleNavExpand}
                        permissionParams={permissionParams}
                        tKey={item.tKey}
                        uiFriendlyTKey={item.uiFriendlyTKey}
                      />
                    </Row>
                  );
                } else if (item.route && item.eventName) {
                  return (
                    <Row noVerticalPadding marginTop='1px'>
                      <NavBarRow
                        label={label}
                        iconPath={item.iconPath}
                        isExternalRoute={item.isExternalRoute}
                        route={item.route}
                        tooltipText={tooltipText}
                        isDisabled={isDisabled}
                        iconPathDark={item.iconPathDark}
                        eventName={item.eventName}
                        isBeta={item.isBeta}
                        tKey={item.tKey}
                        uiFriendlyTKey={item.uiFriendlyTKey}
                      />
                    </Row>
                  );
                } else {
                  return <></>;
                }
              })}
            </>
          );
        })}
        {/* TODO: THIS ONLY FORWARDS BACK TO MOBILE HOME */}
        {/* TODO: ADD THIS TO CONFIG ONCE THIS IS MORE INTEGRATED */}
        {isMobileOnly && (
          <Row noVerticalPadding marginTop='40px'>
            <NavBarRow
              label='Return To Mobile'
              iconPath='/assets/shared/svg/navbar/Return16.svg'
              isExternalRoute={false}
              route={RouteMobileHome}
              tooltipText=''
              isDisabled={false}
              iconPathDark='/assets/shared/svg/navbar/black/Return16.svg'
              eventName='Sidebar - Return To Mobile View'
              customOnClick={() => {
                sessionStorage.setItem('forceDesktop', 'false');
              }}
            />
          </Row>
        )}
      </Rows>
      {RESELLER_CONFIG.title.toLowerCase() === 'ias publisher' && (
        <Div smallHorizontalPadding verticalPadding style={{ position: 'absolute', bottom: '0' }}>
          <Link
            href='https://reporting.integralplatform.com/publisher/'
            target='_blank'
            style={{ color: 'white', paddingLeft: isNavExpanded ? '0px' : '16px' }}
          >
            <img
              src='/assets/shared/svg/navbar/ReturnToLegacy.svg'
              style={{ marginRight: '8px', marginBottom: '-2px' }}
            />
            {isNavExpanded ? 'Return to legacy version' : ''}
          </Link>
        </Div>
      )}
    </NavBarContext.Provider>
  );
};
